import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";

// MUI icons for UI interactions
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";

// Components and functions from project
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonTable from "../../custom-components/CommonTestCasesTable";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  TestCaseFlowDataContext,
  TestCaseLoaderStateContext,
} from "../../main-pages/TestcaseGenerationPage";

// Icons from project
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import {
  generateTestCasesUsingFSDAndUseCase,
  generateTraceabilityMatrix,
} from "../../../services/webServices";

const TestTechniquesComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLoadingState } = useContext(LoadingModalContext);
  const { setTestCaseLoaderState } = useContext(TestCaseLoaderStateContext);
  const {
    useCase,
    testConditions,
    functionalSpecDocument,
    testTechniques,
    testcases,
    setTestCases,
    setSelectedTestingTypes
  } = useContext(TestCaseFlowDataContext);

  const defaultTestTechniquesExpanded =
    Object.keys(testTechniques || {}).length > 0 &&
    Array.isArray(testcases) &&
    testcases.length <= 0;

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);
  const [error, setError] = useState(null);

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleDownload = () => {
    setDownloadOpen(true);
  };

  const handleGenerateTestCases = () => {
    // Mark breadcrumb step as in progress and navigate to selection
    setTestCaseLoaderState("Select Testing Types");
    // Navigate to the new selection step instead of directly generating
    navigate("/dashboard/generate-testcases/select-testing-types", {
      state: { ...location.state },
      replace: true,
    });
    return;
  };

  // const handleGenerateTestCases = async () => {
  //   let fake;

  //   const startProgress = (setProgress) => {
  //     let progress = 0;
  //     let stop = false;
  //     let finalMessageShown = false;

  //     const interval = setInterval(() => {
  //       if (stop) return;

  //       progress += 0.18;
  //       if (progress >= 55 && progress <=75) {
  //         setLoadingState((prev) => ({
  //           ...prev,
  //           message: "Generating Non-Functional Test Cases",
  //         }));
  //       }
  //       else if (progress >=73 && progress <=90  ) {
  //         setLoadingState((prev) => ({
  //           ...prev,
  //           message: "Generating Traceability Matrix",
  //         }));
  //       }
  //       else if (progress >= 98 && !finalMessageShown) {
  //         finalMessageShown = true;
  //         setLoadingState((prev) => ({
  //           ...prev,
  //           message: "Almost done! Fetching your output",
  //         }));
  //       }

  //       if (progress >= 98) progress = 98;
  //       setProgress((prev) => ({
  //         ...prev,
  //         progress: Math.max(prev.progress, Math.floor(progress)),
  //       }));
  //     }, 200); 

  //     return {
  //       stop: () => {
  //         stop = true;
  //         clearInterval(interval);
  //       },
  //       completeTo100: async () => {
  //         stop = true;
  //         clearInterval(interval);

  //         // Rush to 100%
  //         setProgress((prev) => ({
  //           ...prev,
  //           progress: 100,
  //         }));
  //         await new Promise((res) => setTimeout(res, 500));
  //       },
  //     };
  //   };

  //   const finishTo100AndClose = async () => {
  //     await fake.completeTo100();
  //     setLoadingState({ open: false, message: "", progress: null });
  //   };
  //   // Initial message
  //   setLoadingState({
  //     open: true,
  //     message: "Generating Functional Test Cases",
  //     progress: 0,
  //   });

  //   fake = startProgress(setLoadingState);

  //   try {
  //     setError(null);
  //     setTestCaseLoaderState("Test Cases");
  //     setTestCases([]);

  //     const testCases = await generateTestCasesUsingFSDAndUseCase({
  //       testConditions: testConditions,
  //       useCases: useCase,
  //     });

  //     const testCasesDetailsForRTM = testCases.data.testcases.map(
  //       (eachTestCase) => {
  //         return {
  //           "Test Case ID": eachTestCase["Test Case ID"],
  //           "Test Case Title": eachTestCase["Test Case Title"],
  //           "Test Objective": eachTestCase["Test Objective"],
  //         };
  //       }
  //     );

  //     const traceabilityMatrix = await generateTraceabilityMatrix({
  //       functionalSpecDocument:
  //         functionalSpecDocument["Functional Requirements"],
  //       testCaseDetails: testCasesDetailsForRTM,
  //     });
  //     await finishTo100AndClose();
  //     navigate("/dashboard/generate-testcases/output", {
  //       state: {
  //         ...location.state,
  //         testcases: testCases.data?.testcases || [],
  //         traceabilityMatrix: traceabilityMatrix.data || {},
  //       },
  //       replace: true,
  //     });
  //   } catch (error) {
  //     setError("Error generating test cases: " + error.message);
  //     setLoadingState({ open: false, message: "", progress: null });

  //   } finally {

  //     setLoadingState({ open: false, message: "", progress: null });

  //   }
  // };

  const tableDataConverter = (testCases) => {
    const headers = Object.keys(testCases[0]);
    const values = testCases.map((eachTestCase) => {
      return Object.values(eachTestCase);
    });
    return { headers, values };
  };

  // Utility to prepare data for CommonTable
  const handleTestTechniquesTableData = (techniqueArray) => {
    if (!Array.isArray(techniqueArray) || techniqueArray.length === 0) {
      return { headers: [], values: [] };
    }

    const headers = Object.keys(techniqueArray[0]);
    const values = techniqueArray.map((item) => Object.values(item));
    return { headers, values };
  };

  // Generate Inner Accordions for each requirement
  const renderInnerAccordions = () => {
    return Object.entries(testTechniques).map(([requirement, techniques]) => (
      <CommonAccordion
        key={requirement}
        summary={requirement}
        details={
          <CommonTable
            {...handleTestTechniquesTableData(techniques)}
            tableData={techniques}
          />
        }
        type={"inner"}
      />
    ));
  };

  return (
    <Box>
      {/* Main Accordion */}
      <CommonAccordion
        summary={"Test Techniques"}
        details={
          <Box>
            {renderInnerAccordions()}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginTop: 7,
                marginBottom: 2,
              }}
            >
              {/* Thumbs Up Button with Tooltip */}
              <Tooltip title="Like" arrow>
                <Box
                  onClick={() => handleThumbClick("like")}
                  sx={{
                    cursor: "pointer",
                    marginRight: 2,
                    "&:hover": {
                      color: "#49A3FF",
                    },
                  }}
                >
                  {activeThumb === "like" ? (
                    <ThumbUpIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
                  )}
                </Box>
              </Tooltip>

              {/* Thumbs Down Button with Tooltip */}
              <Tooltip title="Dislike" arrow>
                <Box
                  onClick={() => handleThumbClick("dislike")}
                  sx={{
                    cursor: "pointer",
                    marginRight: 2,
                    "&:hover": {
                      color: "#FF4842",
                    },
                  }}
                >
                  {activeThumb === "dislike" ? (
                    <ThumbDownIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
                  )}
                </Box>
              </Tooltip>
              <CommonCopy contentToCopy={testConditions} />
            </Box>
          </Box>
        }
        type={"output"}
        defaultExpanded={defaultTestTechniquesExpanded}
        buttons={[
          {
            label: "Download",
            variant: "text",
            onClick: handleDownload,
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { textTransform: "none" },
          },
          {
            label: "Select Testing Types",
            onClick: handleGenerateTestCases,
            variant: "contained",
            startIcon: <GenerateButton />,
            sx: { float: "right", textTransform: "none" },
          },
        ]}
        fullScreenMode={true}
      />
      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName={"Test Techniques"}
        serviceResponse={[
          ...Object.entries(testTechniques).map(([key, value]) => ({
            heading: key,
            type: "table",
            data: { ...tableDataConverter(value) },
          })),
        ]}
        wordData={[
          ...Object.entries(testTechniques).map(([key, value]) => ({
            heading: key,
            type: "table",
            data: { ...tableDataConverter(value) },
          })),
        ]}
        documents={["docx", "pdf"] || []}
      />
    </Box>
  );
};

export default TestTechniquesComponent;
